import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { ASSETS, buildWhatsAppUrl } from "@/constants/site";

const services = [
  {
    slug: "ressonancia-magnetica",
    title: "Ressonância Magnética Veterinária",
    img: ASSETS.mri,
    lead: "Padrão-ouro para avaliação neurológica, ortopédica e de tecidos moles em cães e gatos.",
    points: [
      "Equipamento Siemens Magnetom C!",
      "Ideal para hérnia de disco, tumores e articulações",
      "Anestesia geral controlada por médico anestesista",
    ],
  },
  {
    slug: "tomografia-computadorizada",
    title: "Tomografia Computadorizada",
    img: ASSETS.tomograph,
    lead: "Imagens em 3D com alta definição para diagnóstico ósseo, torácico e oncológico.",
    points: [
      "Canon Aquilion multi-cortes",
      "Excelente para traumas e planejamento cirúrgico",
      "Opção com contraste iodado",
    ],
  },
  {
    slug: "ultrassonografia",
    title: "Ultrassonografia",
    img: ASSETS.ultrasound,
    lead: "Exame não invasivo, indolor e sem radiação para avaliação abdominal e cardíaca.",
    points: [
      "Ultrassom abdominal completo",
      "Ecodopplercardiograma",
      "Ultrassom guiado para punções",
    ],
  },
  {
    slug: "raio-x-digital",
    title: "Raio-X Digital",
    img: ASSETS.xray,
    lead: "Radiografia digital rápida com envio de laudo por radiologista veterinário.",
    points: [
      "Sistema digital de alta resolução",
      "Menor tempo de exposição",
      "Laudo em curto prazo",
    ],
  },
  {
    slug: "coleta-laboratorial",
    title: "Coleta de Exames Laboratoriais",
    img: ASSETS.bloodDraw,
    lead: "Coleta de líquor guiada e materiais complementares ao diagnóstico por imagem.",
    points: [
      "Punção liquórica na cisterna magna",
      "Coleta com agulha descartável",
      "Encaminhamento a laboratório parceiro",
    ],
  },
];

export default function Services() {
  return (
    <>
      <SEO
        title="Serviços — Ressonância, Tomografia, Ultrassom, Raio-X"
        description="Conheça os exames por imagem veterinários da Visiovet em Belo Horizonte: ressonância magnética, tomografia computadorizada, ultrassom, raio-x digital e coleta laboratorial."
        path="/servicos"
      />

      <section className="bg-white section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mb-3">Serviços</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Exames por imagem <span className="text-brand-500">exclusivamente veterinários</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              A Visiovet é dedicada 100% ao diagnóstico por imagem de cães e gatos. Todos os exames requerem encaminhamento do médico veterinário assistente.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {services.map((s, i) => (
            <motion.div
              key={s.slug}
              id={s.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="rounded-3xl overflow-hidden bg-white border border-slate-200 shadow-sm">
                {s.img ? (
                  <img src={s.img} alt={s.title} className="w-full aspect-[4/3] object-cover" />
                ) : (
                  <div className="w-full aspect-[4/3] bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center">
                    <div className="text-brand-500 font-heading font-bold text-6xl">Lab</div>
                  </div>
                )}
              </div>

              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{s.title}</h2>
                <p className="mt-4 text-slate-600 leading-relaxed">{s.lead}</p>
                <ul className="mt-6 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-brand-500 mt-0.5 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <a
                    href={buildWhatsAppUrl(`Olá Visiovet! Gostaria de informações sobre ${s.title}.`)}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-500 text-white font-semibold px-6 py-3 shadow-lg shadow-brand-500/30 hover:bg-brand-600 hover:-translate-y-0.5 transition-[background-color,transform] duration-200"
                  >
                    Solicitar informações
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
}
